import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ClipboardPen, Plus } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { useReportV2Store } from '@/frontend/store/ReportV2Store';
import { InputSheet, sheetInputs } from '@/reports/inputs';
import { LoadingSpinner } from '../LoadingSpinner';
import { VerdictBadge } from '../runReports/ReportBadges';
import { Badge } from '../ui/badge';
import { Button, buttonVariants } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

/** This org's reports filled in from input sheets, newest first, and the way to start one. */
export function ReportV2ListPage() {
  const reports = useReportV2Store((s) => s.reports);
  const fetchReports = useReportV2Store((s) => s.fetchReports);
  const [showNew, setShowNew] = useState(false);

  useEffect(() => {
    if (reports === null) void fetchReports();
  }, [reports, fetchReports]);
  // a list read on an earlier visit is read again: reports are renamed and their records saved elsewhere
  useEffect(() => {
    if (useReportV2Store.getState().reports !== null) void fetchReports();
  }, [fetchReports]);

  return (
    <div data-slot="reports-v2" className="mx-auto flex w-full max-w-6xl flex-col gap-[1.5rem] px-[1.5rem] py-8 text-sm text-foreground">
      <div className="flex items-start justify-between gap-[1.5rem]">
        <div className="flex flex-col gap-1">
          <h1 className="flex items-center gap-2 text-xl font-semibold"><ClipboardPen className="size-5 text-muted-foreground" /> {I18n.get('rv2Reports')}</h1>
          <p className="max-w-2xl text-muted-foreground">{I18n.get('rv2ReportsHelp')}</p>
        </div>
        <Button className="shrink-0" onClick={() => setShowNew(true)}><Plus /> {I18n.get('rv2NewReport')}</Button>
      </div>

      <div className="overflow-hidden rounded-xl border-[1px] bg-background">
        {reports === null ? (
          <div className="flex justify-center py-10" aria-label={I18n.get('loading')}><LoadingSpinner /></div>
        ) : reports.length === 0 ? (
          <p className="px-[1rem] py-10 text-center text-muted-foreground">{I18n.get('rv2NoReports')}</p>
        ) : (
          <Table>
            <TableHeader className="bg-muted/40">
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-[1rem]">{I18n.get('rv2Title')}</TableHead>
                <TableHead>{I18n.get('rv2Report')}</TableHead>
                <TableHead>{I18n.get('status')}</TableHead>
                <TableHead>{I18n.get('lastSaved')}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {reports.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="min-w-[12rem] pl-[1rem] font-medium whitespace-normal [overflow-wrap:anywhere]"><Link to={`/reports-v2/${r.id}`} className="hover:underline">{r.title}</Link></TableCell>
                  <TableCell className="min-w-[10rem] whitespace-normal">{r.bundleTitle} <span className="text-muted-foreground">· v{r.bundleVersionNumber}</span></TableCell>
                  <TableCell>{r.latest ? <VerdictBadge verdict={r.latest.verdict} label={I18n.get('rv2PublishedVersion', { number: String(r.latest.number), verdict: r.latest.verdictLabel })} /> : <Badge variant="outline">{I18n.get('rv2Draft')}</Badge>}</TableCell>
                  <TableCell className="min-w-[10rem] text-xs whitespace-normal text-muted-foreground">{new Date(r.updatedAt).toLocaleString()} · <span className="[overflow-wrap:anywhere]">{r.updatedBy}</span></TableCell>
                  <TableCell className="pr-[1rem] text-right">
                    <Link to={`/reports-v2/${r.id}`} className={buttonVariants({ variant: 'outline', size: 'sm' })}>{I18n.get('open')} <ArrowRight /></Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      <Dialog open={showNew} onOpenChange={setShowNew}>{showNew && <NewReportV2Dialog onDone={() => setShowNew(false)} />}</Dialog>
    </div>
  );
}

const today = () => new Date().toISOString().slice(0, 10);
/** A title for a new report: the report's name, shortened at its first dash, and today's date. */
const suggestTitle = (title: string) => `${title.split(/\s+[—–-]\s+/)[0]} · ${today()}`;

type StartFrom = 'empty' | 'copy' | 'same';

/**
 * Pick a report, name this one, and start from an empty sheet, a copy of a
 * previous report's record, or the very record another report renders — one
 * record, more than one report. Given `onRecord`, it starts a report on that
 * record: only reports on its schema are offered.
 */
export function NewReportV2Dialog({ onDone, onRecord }: { onDone: () => void; onRecord?: { recordId: string; schemaId: string; title: string } }) {
  const startable = useReportV2Store((s) => s.startable);
  const fetchStartable = useReportV2Store((s) => s.fetchStartable);
  const reports = useReportV2Store((s) => s.reports);
  const create = useReportV2Store((s) => s.create);
  const navigate = useNavigate();
  const [versionId, setVersionId] = useState('');
  const [title, setTitle] = useState('');
  const [titleEdited, setTitleEdited] = useState(false);
  const [startFrom, setStartFrom] = useState<StartFrom>(onRecord ? 'same' : 'empty');
  const [copyFromId, setCopyFromId] = useState('');
  const [sameRecordId, setSameRecordId] = useState('');
  const [showSheet, setShowSheet] = useState(false);
  const [busy, setBusy] = useState(false);

  const fetchReports = useReportV2Store((s) => s.fetchReports);
  const offered = useMemo(() => (startable ?? []).filter((r) => !onRecord || r.schemaId === onRecord.schemaId), [startable, onRecord?.schemaId]);
  useEffect(() => {
    if (reports === null) void fetchReports();
    void fetchStartable().then((list) => {
      const first = list.find((r) => !onRecord || r.schemaId === onRecord.schemaId);
      if (first) {
        setVersionId((v) => v || first.reportBundleVersionId);
        setTitle((t) => t || suggestTitle(first.title));
      }
    });
  }, [fetchStartable]);

  const chosen = offered.find((r) => r.reportBundleVersionId === versionId) ?? null;
  // a record carries over to any report on the same schema; whether this version can read one is the server's to say
  const previous = useMemo(() => (reports ?? []).filter((r) => r.schemaId === chosen?.schemaId), [reports, chosen?.schemaId]);
  // each record once, named by every report already on it
  const records = useMemo(() => {
    const byRecord = new Map<string, string[]>();
    for (const r of previous) byRecord.set(r.recordId, [...(byRecord.get(r.recordId) ?? []), r.title]);
    return [...byRecord.entries()].map(([id, titles]) => ({ id, label: titles.join(' / ') }));
  }, [previous]);
  const recordId = onRecord?.recordId ?? (sameRecordId || null);

  const choose = (id: string) => {
    setVersionId(id);
    setCopyFromId('');
    setSameRecordId('');
    const next = startable?.find((r) => r.reportBundleVersionId === id);
    if (next && !titleEdited) setTitle(suggestTitle(next.title));
  };

  const start = async () => {
    if (!chosen) return;
    setBusy(true);
    try {
      const report = await create({
        reportBundleVersionId: chosen.reportBundleVersionId,
        title,
        copyFromId: startFrom === 'copy' && copyFromId ? copyFromId : null,
        recordId: startFrom === 'same' ? recordId : null,
      });
      if (report) {
        onDone();
        void navigate(`/reports-v2/${report.id}`);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent data-slot="reports-v2" className="sm:max-w-lg">
      <DialogHeader>
        <DialogTitle>{onRecord ? I18n.get('rv2AnotherReportOnRecord') : I18n.get('rv2NewManualReport')}</DialogTitle>
        <DialogDescription>{onRecord ? I18n.get('rv2AnotherReportOnRecordHelp', { title: onRecord.title }) : I18n.get('rv2NewManualReportHelp')}</DialogDescription>
      </DialogHeader>
      {startable === null ? (
        <div className="flex justify-center py-6"><LoadingSpinner /></div>
      ) : offered.length === 0 ? (
        <p className="text-muted-foreground">{I18n.get('rv2NothingStartable')}</p>
      ) : (
        <div className="flex flex-col gap-[1rem]">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-report-bundle">{I18n.get('rv2Report')}</Label>
            <select id="new-report-bundle" value={versionId} onChange={(e) => choose(e.target.value)} className="h-8 w-full rounded-lg border-[1px] border-input bg-background px-2 text-sm">
              {offered.map((r) => <option key={r.reportBundleVersionId} value={r.reportBundleVersionId}>{r.title} · v{r.versionNumber}</option>)}
            </select>
            {chosen && (
              <p className="text-xs text-muted-foreground">
                {I18n.get('rv2SheetSize', { inputs: String(sheetInputs(chosen.sheet).length), sections: String(chosen.sheet.sections.length) })}{' '}
                <button type="button" className="text-sky-800 underline underline-offset-2" onClick={() => setShowSheet((s) => !s)}>{showSheet ? I18n.get('rv2HideSheet') : I18n.get('rv2ViewSheet')}</button>
              </p>
            )}
            {chosen && showSheet && <SheetOutline sheet={chosen.sheet} />}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-report-title">{I18n.get('rv2Title')}</Label>
            <Input id="new-report-title" value={title} onChange={(e) => { setTitle(e.target.value); setTitleEdited(true); }} />
          </div>
          {!onRecord && (
          <div className="flex flex-col gap-1.5">
            <Label id="new-report-start">{I18n.get('rv2StartFrom')}</Label>
            <RadioGroup aria-labelledby="new-report-start" value={startFrom} onValueChange={(v) => setStartFrom(v as StartFrom)}>
              <label className="flex cursor-pointer items-start gap-[0.75rem] rounded-lg border-[1px] p-[0.75rem] has-data-checked:border-sky-700 has-data-checked:bg-sky-50">
                <RadioGroupItem value="empty" className="mt-0.5" />
                <span className="flex flex-col"><span className="font-medium">{I18n.get('rv2EmptySheet')}</span><span className="text-xs text-muted-foreground">{I18n.get('rv2EmptySheetHelp')}</span></span>
              </label>
              <label className="flex cursor-pointer items-start gap-[0.75rem] rounded-lg border-[1px] p-[0.75rem] has-data-checked:border-sky-700 has-data-checked:bg-sky-50">
                <RadioGroupItem value="copy" className="mt-0.5" disabled={previous.length === 0} />
                <span className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <span className="font-medium">{I18n.get('rv2CopyPrevious')}</span>
                  <span className="text-xs text-muted-foreground">{previous.length ? I18n.get('rv2CopyPreviousHelp') : I18n.get('rv2NoPrevious')}</span>
                  {startFrom === 'copy' && previous.length > 0 && (
                    <select aria-label={I18n.get('rv2CopyPrevious')} value={copyFromId} onChange={(e) => setCopyFromId(e.target.value)} className="h-8 w-full rounded-lg border-[1px] border-input bg-background px-2 text-sm">
                      <option value="">{I18n.get('rv2ChoosePrevious')}</option>
                      {previous.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
                    </select>
                  )}
                </span>
              </label>
              <label className="flex cursor-pointer items-start gap-[0.75rem] rounded-lg border-[1px] p-[0.75rem] has-data-checked:border-sky-700 has-data-checked:bg-sky-50">
                <RadioGroupItem value="same" className="mt-0.5" disabled={records.length === 0} />
                <span className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <span className="font-medium">{I18n.get('rv2SameRecord')}</span>
                  <span className="text-xs text-muted-foreground">{records.length ? I18n.get('rv2SameRecordHelp') : I18n.get('rv2NoPrevious')}</span>
                  {startFrom === 'same' && records.length > 0 && (
                    <select aria-label={I18n.get('rv2SameRecord')} value={sameRecordId} onChange={(e) => setSameRecordId(e.target.value)} className="h-8 w-full rounded-lg border-[1px] border-input bg-background px-2 text-sm">
                      <option value="">{I18n.get('rv2ChoosePrevious')}</option>
                      {records.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                    </select>
                  )}
                </span>
              </label>
            </RadioGroup>
          </div>
          )}
        </div>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={onDone}>{I18n.get('cancel')}</Button>
        <Button disabled={!chosen || !title.trim() || busy || (startFrom === 'copy' && !copyFromId) || (startFrom === 'same' && !recordId)} onClick={() => void start()}>{busy ? I18n.get('saving') : I18n.get('rv2StartReport')}</Button>
      </DialogFooter>
    </DialogContent>
  );
}

/** A sheet at a glance: its sections and what each asks. */
function SheetOutline({ sheet }: { sheet: InputSheet }) {
  return (
    <div className="max-h-56 overflow-y-auto rounded-lg border-[1px] bg-muted/30 px-[0.75rem] py-2 text-xs">
      {sheet.sections.map((s) => (
        <div key={s.id} className="mb-1.5 last:mb-0">
          <p className="font-medium">{s.title}</p>
          <p className="text-muted-foreground">{s.inputs.map((i) => `${i.label}${i.required ? ' *' : ''}`).join(' · ')}</p>
        </div>
      ))}
    </div>
  );
}
